import React, { useEffect, useState } from 'react';
import {
  Activity,
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
  MinusCircle,
} from 'lucide-react';
import { formatSnapshotDate } from '../utils/dateUtils';

const API_BASE = import.meta.env.VITE_PUBLIC_DATA_ENDPOINT?.replace(/\/$/, '') || '';

function apiUrl(path) {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE}${cleanPath}`;
}

function normalizeSummaryDates(text) {
  if (!text) return text;
  return String(text).replace(
    /\b(\d{4})-(\d{2})-(\d{2})\b/g,
    (_match, year, month, day) => `${month}-${day}-${year}`
  );
}

function toneClasses(tone) {
  if (tone === 'positive') {
    return 'border-mentor-green/30 bg-mentor-green/10 text-mentor-green';
  }

  if (tone === 'negative') {
    return 'border-red-400/30 bg-red-500/10 text-red-100';
  }

  return 'border-mentor-border/50 bg-mentor-bg/35 text-mentor-text';
}

function ToneIcon({ tone }) {
  if (tone === 'positive') return <CheckCircle2 size={16} />;
  if (tone === 'negative') return <AlertTriangle size={16} />;
  return <MinusCircle size={16} />;
}

function formatValue(value, unit) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '—';
  }

  if (unit === 'bps') {
    return `${Number(value) >= 0 ? '+' : ''}${value} bps`;
  }

  if (unit === '%') {
    return `${Number(value).toFixed(2)}%`;
  }

  if (unit === 'pts') {
    return `${Math.round(Number(value))} pts`;
  }

  return String(value);
}

function ComparisonBadge({ data }) {
  const hasPreviousDate = Boolean(data?.previousDate);
  const currentDate = data?.currentDate ? formatSnapshotDate(data.currentDate) : null;
  const previousDate = hasPreviousDate ? formatSnapshotDate(data.previousDate) : null;
  const status = data?.status || '—';

  return (
    <div className="rounded-xl border border-mentor-border/40 bg-mentor-bg/35 px-4 py-3 text-xs text-mentor-muted w-full lg:w-56 shrink-0">
      <div className="text-[10px] uppercase tracking-wider font-display font-semibold text-mentor-muted mb-1">
        {hasPreviousDate ? 'Compared' : 'Snapshot'}
      </div>

      {hasPreviousDate ? (
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-3">
            <span className="text-mentor-muted">From</span>
            <span className="font-display font-bold text-mentor-text whitespace-nowrap">
              {previousDate}
            </span>
          </div>

          <div className="flex items-center justify-between gap-3">
            <span className="text-mentor-muted">To</span>
            <span className="font-display font-bold text-mentor-text whitespace-nowrap">
              {currentDate || '—'}
            </span>
          </div>
        </div>
      ) : (
        <div className="font-display font-bold text-mentor-text whitespace-nowrap">
          {currentDate || 'Building history'}
        </div>
      )}

      <div className="mt-2 pt-2 border-t border-mentor-border/30">
        <span>Status: </span>
        <span className="font-display font-semibold text-mentor-text">
          {status}
        </span>
      </div>
    </div>
  );
}

export default function WhatChangedSummary() {
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();

    async function loadWhatChanged() {
      try {
        setError('');

        const response = await fetch(apiUrl('/api/what-changed'), {
          method: 'GET',
          credentials: 'include',
          signal: controller.signal,
          headers: {
            Accept: 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`What Changed endpoint returned ${response.status}`);
        }

        const json = await response.json();

        if (!controller.signal.aborted) {
          setPayload(json?.whatChanged || null);
        }
      } catch (err) {
        if (controller.signal.aborted) return;

        setError(err?.message || 'What Changed summary unavailable.');
      }
    }

    loadWhatChanged();

    return () => {
      controller.abort();
    };
  }, []);

  const data = payload;

  return (
    <div className="glass-card p-5 border border-mentor-accent/20">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-mentor-accent/10 border border-mentor-accent/20 p-3 text-mentor-accent">
            <Activity size={20} />
          </div>

          <div>
            <h3 className="font-display font-bold text-xl text-mentor-text">
              What Changed?
            </h3>

            <p className="text-sm text-mentor-muted leading-relaxed mt-1">
              {error ||
                normalizeSummaryDates(data?.summary) ||
                'Loading the latest clean snapshot comparison…'}
            </p>
          </div>
        </div>

        {data?.currentDate && <ComparisonBadge data={data} />}
      </div>

      {data?.available && Array.isArray(data.metrics) && data.metrics.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {data.metrics.map((metric) => (
            <div
              key={metric.key}
              className={`rounded-2xl border p-4 ${toneClasses(metric.tone)}`}
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <p className="text-[10px] uppercase tracking-wider font-display font-bold opacity-80">
                  {metric.label}
                </p>

                <ToneIcon tone={metric.tone} />
              </div>

              <div className="flex items-baseline gap-2 font-mono">
                <span className="text-sm opacity-80">
                  {formatValue(metric.previous, metric.unit)}
                </span>

                <ArrowRight size={14} className="opacity-60" />

                <span className="text-lg font-bold">
                  {formatValue(metric.current, metric.unit)}
                </span>
              </div>

              <p className="text-sm font-display font-semibold mt-2">
                {metric.deltaLabel}
              </p>

              <p className="text-xs opacity-80 mt-1">
                {metric.classification}
              </p>
            </div>
          ))}
        </div>
      )}

      {Array.isArray(data?.drivers) && data.drivers.length > 0 && (
        <div className="mt-4 rounded-2xl border border-mentor-border/40 bg-mentor-bg/35 p-4">
          <p className="text-[10px] text-mentor-muted uppercase tracking-wider font-display font-semibold mb-2">
            Why risk changed
          </p>

          <ul className="space-y-1 text-sm text-mentor-muted">
            {data.drivers.slice(0, 5).map((driver) => (
              <li key={driver}>• {driver}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
